import {
  Alert,
  AlertIcon,
  Button,
  ButtonGroup,
  Center,
  Flex,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import uniq from "lodash/uniq";
import React, { useMemo, useState } from "react";
import {
  FormProvider,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";

import { Restricted } from "@/app/components/Restricted";
import { ConfirmationDialog } from "@/common/components/ConfirmationDialog";
import { DelayedSpinner } from "@/common/components/DelayedSpinner";
import EmptyState from "@/common/components/EmptyState";
import FormReset from "@/common/components/Form/FormReset";
import { FormRootError } from "@/common/components/Form/FormRootError";
import FormSubmit from "@/common/components/Form/FormSubmit";
import FormTextArea from "@/common/components/Form/FormTextArea";
import { IcoRemoteConfig } from "@/common/components/Icons";
import { MotionRule } from "@/common/components/MotionComponents";
import { SectionHeading } from "@/common/components/SectionHeading";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useFormMutationSubmitHandler } from "@/common/hooks/useApiForm";
import { useSlugParam } from "@/common/hooks/useParam";
import { useEnvironmentsData } from "@/environment/data/useEnvironmentsData";
import { FeatureConfigModal } from "@/feature/components/FeatureConfigModal";
import { useFeatureConfigData } from "@/feature/data/useFeatureConfigData";
import { useFeatureConfigUpdateMutation } from "@/feature/data/useFeatureConfigUpdateMutation";
import { useFeatureData } from "@/feature/data/useFeatureData";
import { useFlag } from "@reflag/react-sdk";
import {
  featureConfigToUpdate,
  FeatureConfigVariant,
  UpdateFeatureConfig,
  UpdateFeatureConfigSchema,
} from "@reflag/shared/featureConfigAPI";
import { filterMap } from "@reflag/shared/utils/array";

import { FeatureConfig } from "./FeatureConfig";

const listFormatter = new Intl.ListFormat("en", {
  style: "long",
  type: "conjunction",
});

export const FeatureConfigs = () => {
  const { featureId } = useSlugParam("feature");
  const { envId } = useEnvironment();

  const configDialog = useDisclosure();
  const confirmChangesDialog = useDisclosure();
  const [selectedVariant, setSelectedVariant] = useState<{
    variant: FeatureConfigVariant;
    index: number;
  } | null>(null);
  const { requestFeedback } = useFlag("dynamic-configs");

  const {
    data: configData,
    isLoading: isConfigLoading,
    isError: isConfigError,
  } = useFeatureConfigData(featureId);

  const {
    data: environments = [],
    isLoading: isEnvironmentsLoading,
    isError: isEnvironmentError,
  } = useEnvironmentsData();

  const { data: feature } = useFeatureData(featureId!);

  const [
    expectedVersionIds,
    defaultRuleIndexes,
    variantsWithProductionTargeting,
  ] = useMemo(() => {
    const prodEnvIds = environments
      .filter((e) => e.isProduction)
      .map((e) => e.id);
    return [
      configData ? Object.values(configData).map((config) => config.id) : [],
      configData
        ? uniq(
            Object.entries(configData).flatMap(
              ([environmentId, { variants }]) => {
                const environment = environments.find(
                  (e) => e.id === environmentId,
                );
                const index = variants.findIndex(
                  ({ rule: { default: isDefault } }) => isDefault,
                );
                return environment && index > -1
                  ? [{ environment, index }]
                  : [];
              },
            ),
          )
        : [],
      configData
        ? Object.entries(configData)
            .filter(([envId]) => prodEnvIds.includes(envId))
            .flatMap(([, config]) => config.variants)
            .filter(
              ({ rule }) =>
                rule.default ||
                rule.segmentIds.length ||
                rule.companyIds.length ||
                rule.customRules?.length ||
                rule.userIds.length,
            )
            .map(({ variant: { key } }) => key)
        : [],
    ];
  }, [configData, environments]);

  const createConfigVersionsMutation = useFeatureConfigUpdateMutation(
    featureId!,
    expectedVersionIds.length ? (expectedVersionIds as [string]) : undefined,
  );

  const currentConfig = useMemo(
    () =>
      configData
        ? featureConfigToUpdate(configData)
        : {
            changeDescription: "",
            variants: [],
          },
    [configData],
  );

  const form = useForm<UpdateFeatureConfig>({
    resolver: zodResolver(UpdateFeatureConfigSchema),
    values: currentConfig,
  });

  const { fields, append, remove, update } = useFieldArray({
    control: form.control,
    name: "variants",
  });

  const variants = useWatch({
    control: form.control,
    name: "variants",
  });

  const handleConfigSubmit = (variant: FeatureConfigVariant) => {
    if (selectedVariant) {
      // Update existing variant
      update(selectedVariant.index, {
        ...variants[selectedVariant.index],
        variant,
      });
    } else {
      // Add new variant
      append({
        environmentRules: Object.fromEntries(
          environments.map(({ id }) => [
            id,
            {
              segmentIds: [],
              companyIds: [],
              userIds: [],
              customRules: [],
              default: fields.length === 0, // Set first variant as default
            },
          ]),
        ),
        variant,
      });
    }
    configDialog.onClose();
    setSelectedVariant(null);
  };

  const handleSetDefault = (index: number) => {
    variants.forEach((variant, i) => {
      update(i, {
        ...variant,
        environmentRules: {
          ...variant.environmentRules,
          [envId!]: {
            ...variant.environmentRules[envId!],
            default: i === index,
          },
        },
      });
    });
  };

  const onSaveClick = form.handleSubmit(() => {
    confirmChangesDialog.onOpen();
  });

  const onConfirmClick = useFormMutationSubmitHandler(
    form,
    createConfigVersionsMutation,
    (data) => {
      form.reset(featureConfigToUpdate(data));
      confirmChangesDialog.onClose();
    },
    {
      prepareVariables: (values) => ({
        ...values,
        variants: values.variants.map((config) => ({
          ...config,
          variant: {
            ...config.variant,
            // Payload should be null if empty string
            payload:
              typeof config.variant.payload === "string"
                ? config.variant.payload.trim() || null
                : config.variant.payload,
          },
          environmentRules: {
            // Send only the rules for the current environment
            [envId!]: {
              ...config.environmentRules[envId!],
            },
          },
        })),
      }),
      successToast: "Flag remote config updated",
      errorToast: "Failed to update flag remote config",
    },
  );

  const currentEnvironment = environments.find((e) => e.id === envId);
  const noConfig = !fields.length && !form.formState.isDirty;

  return (
    <Restricted
      alertStyle="callout"
      displayContents={noConfig ? "inert" : "normal"}
      feature={feature}
      role={currentEnvironment?.isProduction ? "publisher" : "contributor"}
    >
      <FormProvider {...form}>
        {isConfigLoading || isEnvironmentsLoading ? (
          <Center flexGrow={1}>
            <DelayedSpinner />
          </Center>
        ) : isConfigError || isEnvironmentError ? (
          <Alert status="error">
            <AlertIcon />
            Failed to load remote configurations, please reload the page.
          </Alert>
        ) : !fields.length && !form.formState.isDirty ? (
          <Center flexGrow={1}>
            <EmptyState
              action={
                <Button
                  onClick={() => {
                    setSelectedVariant(null);
                    configDialog.onOpen();
                  }}
                >
                  Create config variant
                </Button>
              }
              description={
                <Text>
                  Serve arbitrary configuration to customers and easily change
                  it without re-deploying.
                </Text>
              }
              flexGrow={1}
              icon={<IcoRemoteConfig boxSize="48px" />}
              maxW="md"
              title="Start using remote config"
            />
          </Center>
        ) : (
          <Flex as="form" direction="column" onSubmit={onSaveClick}>
            <Flex direction="column" gap={2} mb={6}>
              <SectionHeading
                _container={{ minH: 8 }}
                rightElement={
                  <Button
                    colorScheme="brand"
                    variant="ghost"
                    onClick={(e) => {
                      e.preventDefault();
                      requestFeedback({
                        title: "How do you like remote configs?",
                        position: {
                          type: "POPOVER",
                          anchor: e.currentTarget,
                        },
                        openWithCommentVisible: true,
                      });
                    }}
                  >
                    Give feedback
                  </Button>
                }
              >
                Config variants
              </SectionHeading>
              <Text color="dimmed" fontSize="sm">
                Config variants are shared across environments, whereas
                targeting rules are specific to each environment.
                <br />
                Targeting is evaluated in the following order: users, companies,
                segments, and finally rules.
              </Text>
            </Flex>
            <Flex align="flex-start" direction="column" gap={4}>
              {fields.map((field, index) => {
                const environmentDefaults = filterMap(
                  defaultRuleIndexes,
                  ({ environment, index: defaultIndex }) =>
                    defaultIndex === index ? environment.name : null,
                );
                const deleteDisabledReason =
                  environmentDefaults.length > 0 && fields.length > 1
                    ? `This config variant is set as default in ${listFormatter.format(environmentDefaults)}.
                       Please set a new default value in these environments before deleting this one.`
                    : undefined;
                return (
                  <MotionRule key={field.id}>
                    <FeatureConfig
                      key={field.id}
                      baseName={`variants.${index}`}
                      deleteDisabled={deleteDisabledReason}
                      impactsProduction={
                        currentEnvironment?.isProduction ||
                        variantsWithProductionTargeting.includes(
                          field.variant.key,
                        )
                      }
                      index={index}
                      isOnlyConfig={fields.length === 1}
                      onDelete={() => remove(index)}
                      onEdit={() => {
                        const variant = form.getValues(
                          `variants.${index}.variant`,
                        );
                        setSelectedVariant({ variant, index });
                        configDialog.onOpen();
                      }}
                      onSetDefault={() => handleSetDefault(index)}
                    />
                  </MotionRule>
                );
              })}

              <Restricted
                alertStyle="none"
                feature={feature}
                role={
                  currentEnvironment?.isProduction ? "publisher" : "contributor"
                }
              >
                <Flex direction="column" gap={8}>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSelectedVariant(null);
                      configDialog.onOpen();
                    }}
                  >
                    Add config variant
                  </Button>

                  <FormRootError />

                  <ButtonGroup>
                    <FormSubmit
                      isLoading={createConfigVersionsMutation.isPending}
                      disableOnPristine
                      showTooltipOnPristine
                    />
                    <FormReset />
                  </ButtonGroup>
                </Flex>
              </Restricted>
            </Flex>
          </Flex>
        )}

        <FeatureConfigModal
          {...configDialog}
          key={selectedVariant?.index}
          existingKeys={fields
            .map((field) => field.variant.key)
            .filter((key) => key !== selectedVariant?.variant.key)}
          variant={selectedVariant?.variant}
          onClose={() => {
            configDialog.onClose();
            setSelectedVariant(null);
          }}
          onSubmit={handleConfigSubmit}
        />

        <ConfirmationDialog
          {...confirmChangesDialog}
          _contentProps={{ maxW: "2xl" }}
          description={
            <VStack spacing={4} w="full">
              <FormTextArea
                name="changeDescription"
                placeholder="Add a description of the changes..."
              />
              <FormRootError />
            </VStack>
          }
          isLoading={createConfigVersionsMutation.isPending}
          title={<Text whiteSpace="nowrap">Confirm remote config changes</Text>}
          onCancel={() => {
            form.resetField("changeDescription");
            confirmChangesDialog.onClose();
          }}
          onConfirm={onConfirmClick}
        />
      </FormProvider>
    </Restricted>
  );
};
